import React, { useEffect, useState } from 'react';
import useUser from '../hooks/useUser';
import { FreelancerExperienceLevel, FreelancerRole, IFreelancer, RestrictedFreelancerRole } from '../data_models/freelancer';
import useDisplayNotifications from '../hooks/useDisplayNotifications';

import { DEFAULT_USER_PROFILE } from '../config/defaultAssetUrls';
import { EUserType } from '../data_models/enums';
import useCurrentUser from '../hooks/useCurrentUser';
import { UserEnumTextMapper } from '../data_models/enumToTextMapper';
import useGlobalLoading from '../hooks/useGlobalLoading';

import { getFreelancerName } from '../utils/freelancerUtils';
import RichText from '../components/common/RichText';
import FreelancerProfileDrawer from '../components/drawers/FreelancerProfileDrawer';
import Pagination from '../components/common/Pagination';
import { OutlinedDropdownMenu } from '../components/common/DropdownMenu';



const BrowseFreelancersPage: React.FC = () => {


    const [standardFilter, setStandardFilter] = useState<any>({
        _profileCompletedPercentage: { $gte: 50 }
    });
    const [filter, setFilter] = useState(standardFilter);

    const { currentUser, currentUserType } = useCurrentUser();
    const { setLoading } = useGlobalLoading();

    const [filterSkillsOrTools, setFilterSkillsOrTools] = useState([] as string[]);
    const [filterSkillsOrToolsExperience, setFilterSkillsOrToolsExperience] = useState(FreelancerExperienceLevel.T1);

    // const [loading, setLoading] = useState(false);
    const [freelancers, setFreelancers] = useState([] as IFreelancer[]);

    const { fetchFreelancersByFilter } = useUser();
    const { addNotification } = useDisplayNotifications();

    // set page
    const [freelancersPage, setFreelancersPage] = useState(1);
    const [freelancersLimit, setFreelancersLimit] = useState(12);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [totalPages, setTotalPages] = useState(0);


    const [openFreelancerProfileDrawer, setOpenFreelancerProfileDrawer] = useState(false);
    const [selectedFreelancer, setSelectedFreelancer] = useState<IFreelancer | null>(null);


    const applyFilter = () => {
        if (filterSkillsOrTools.length > 0) {
            const filter: any = {
                $and: [
                    {
                        _profileCompletedPercentage: { $gte: 50 }
                    },
                    {
                        $or: [
                            {
                                skills: {
                                    $elemMatch: {
                                        skill: {
                                            $regex: filterSkillsOrTools.map(skill => `.*${skill}.*`).join('|'),
                                            $options: "i"
                                        },

                                    },
                                }
                            },
                            {
                                tools: {
                                    $elemMatch: {
                                        tool: {
                                            $regex: filterSkillsOrTools.map(tool => `.*${tool}.*`).join('|'),
                                            $options: "i"
                                        },
                                    },
                                },
                            },
                        ]
                    }
                ]
            };
            setFilter(filter);
        } else {
            setFilter(standardFilter);
        }
    }


    useEffect(() => {
        if (currentUser && currentUserType === EUserType.FREELANCER && (currentUser as IFreelancer).restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)) {
            setStandardFilter({});
        } else {
            setStandardFilter({
                _profileCompletedPercentage: { $gte: 50 }
            });
        }
    }, [currentUser, currentUserType]);

    useEffect(() => {
        setLoading(true);
        fetchFreelancersByFilter(filter, freelancersPage, freelancersLimit)
            .then((result: { freelancers: IFreelancer[], totalPages: number, totalDocuments: number }) => {
                // sort freelancers by _createdAt (newest first) 
                result.freelancers.sort((a: IFreelancer, b: IFreelancer) => {
                    return new Date(b._createdAt!).getTime() - new Date(a._createdAt!).getTime();
                });
                setFreelancers(result.freelancers);
                setTotalDocuments(result.totalDocuments);
                setTotalPages(result.totalPages);
            })
            .catch((error: any) => {
            })
            .finally(() => {
                setLoading(false);
            });
    }, [filter, freelancersPage, freelancersLimit]);

    return (
        <div className="relative min-h-screen w-full overflow-hidden p-4">
            {/* Blurred dots */}
            <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-primary-500/30 blur-3xl -z-10" />
            <div className="absolute -top-12 left-64 w-64 h-64 rounded-full bg-primary-700/25 blur-3xl -z-10" />
            <div className="absolute -bottom-10 -right-5 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl -z-10" />
            <div className="absolute bottom-36 right-5 w-64 h-64 rounded-full bg-secondary-500/20 blur-3xl -z-10" />
            <div className="absolute bottom-12 left-12 w-48 h-48 rounded-full bg-primary-200/30 blur-3xl -z-10" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-secondary-300/20 blur-3xl -z-10" />

            <div className="p-4 max-w-7xl mx-auto">

                {/* Header section */}
                <div className="flex flex-col md:flex-row justify-between md:items-center">
                    <h2 className="text-2xl/7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">Talent</h2>
                    <div className="flex flex-row space-x-2 mt-4 md:mt-0">
                        {/* <div className="min-w-36">
                            <OutlinedDropdownMenu
                                options={filterOptions}
                                onSelect={(option) => setActiveFilter(option)}
                                defaultValue={activeFilter}
                            />
                        </div>
                        <div className="min-w-36">
                            <OutlinedDropdownMenu
                                options={sortOptions}
                                onSelect={(option) => setActiveSort(option)}
                                defaultValue={activeSort}
                            />
                        </div> */}
                    </div>
                </div>

                {/* Freelancers */}
                <ul role="list" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {freelancers.map((freelancer) => (
                        <li
                            key={freelancer._id}
                            className="col-span-1 flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow-md border border-gray-100 hover:scale-105 transition-all duration-300 cursor-pointer"
                            onClick={() => {
                                setSelectedFreelancer(freelancer);
                                setOpenFreelancerProfileDrawer(true);
                            }}
                        >
                            <div className="flex flex-1 flex-col p-8">
                                <img alt=""
                                    src={freelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE}
                                    className="mx-auto size-32 shrink-0 rounded-full object-cover"
                                />
                                <h3 className="mt-6 text-sm font-medium text-gray-900">{getFreelancerName(freelancer)}</h3>
                                <dl className="mt-1 flex grow flex-col justify-between">
                                    <dt className="sr-only">Roles</dt>
                                    <dd className="text-sm text-gray-500">{(freelancer.roles || [FreelancerRole.OTHER]).map(role => UserEnumTextMapper.freelancerRole(role)).join(", ")}</dd>
                                    <dt className="sr-only">Bio</dt>
                                    <dd className="mt-3 line-clamp-2">
                                        <RichText text={freelancer.bio || ''} />
                                    </dd>
                                </dl>
                            </div>

                        </li>
                    ))}
                </ul>

                {/* Pagination */}
                <Pagination
                    currentPage={freelancersPage}
                    totalPages={totalPages}
                    totalItems={totalDocuments}
                    itemsPerPage={freelancersLimit}
                    onPageChange={setFreelancersPage}
                />
            </div>

            {selectedFreelancer && (
                <FreelancerProfileDrawer
                    freelancer={selectedFreelancer}
                    open={openFreelancerProfileDrawer}
                    setOpen={setOpenFreelancerProfileDrawer}
                />
            )}
        </div>
    );
};
export default BrowseFreelancersPage;
